import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useOrderSocket } from '../hooks/useOrderSocket';
import { playOrderAlertSound } from '../utils/playOrderAlertSound';

const MAX_ORDER_ALERTS = 30;

const getOrderNumberFromMessage = (message = {}) => {
  const sk = String(message.SK || '');
  if (sk.includes('#ORDER#')) return sk.split('#ORDER#')[1] || message.OrderId || 'N/A';
  return message.OrderNumber || message.orderNumber || message.OrderId || 'N/A';
};

const getOrderAlertLabel = (type) => {
  switch (type) {
    case 'ORDER_CREATED':
      return 'New order';
    case 'ORDER_STATUS_UPDATED':
      return 'Status update';
    case 'ORDER_PAYMENT_UPDATED':
      return 'Payment update';
    case 'ORDER_NOTES_UPDATED':
      return 'Order note';
    case 'ORDER_NOTES_READ':
      return 'Notes read';
    default:
      return 'Order update';
  }
};

const getOrderAlertDetail = (message = {}) => {
  const nextStatus = message.newStatus || message.OrderStatus;
  switch (message.type) {
    case 'ORDER_CREATED':
      return 'New order received';
    case 'ORDER_STATUS_UPDATED':
      return nextStatus ? `Status changed to ${nextStatus}` : 'Order status changed';
    case 'ORDER_PAYMENT_UPDATED':
      return message.PaymentStatus ? `Payment updated: ${message.PaymentStatus}` : 'Payment updated';
    case 'ORDER_NOTES_UPDATED':
      return 'New message received';
    case 'ORDER_NOTES_READ':
      return 'Messages marked as read';
    default:
      return nextStatus ? `Latest status: ${nextStatus}` : 'Order details updated';
  }
};

const buildOrderAlert = (message) => ({
  id: `${message.type || 'ORDER'}#${message.sentAt || Date.now()}#${getOrderNumberFromMessage(message)}`,
  type: message.type || 'ORDER_UPDATED',
  label: getOrderAlertLabel(message.type),
  orderNumber: getOrderNumberFromMessage(message),
  detail: getOrderAlertDetail(message),
  receivedAt: message.sentAt || new Date().toISOString(),
  message,
});

const OrderSocketContext = createContext({
  lastMessage: null,
  messageVersion: 0,
  pendingOrderAlertCount: 0,
  orderAlerts: [],
  latestOrderAlert: null,
  acknowledgeOrderEvents: () => {},
  isConnected: false,
  error: null,
});

export const OrderSocketProvider = ({ children }) => {
  const { token } = useAuth();
  const { lastMessage, isConnected, error } = useOrderSocket(token, 'admin', Boolean(token));
  const [messageVersion, setMessageVersion] = useState(0);
  const [orderAlerts, setOrderAlerts] = useState([]);

  useEffect(() => {
    if (!lastMessage) return;
    setMessageVersion((currentVersion) => currentVersion + 1);
    if (lastMessage.messageCategory === 'ORDER') {
      setOrderAlerts((currentAlerts) => [buildOrderAlert(lastMessage), ...currentAlerts].slice(0, MAX_ORDER_ALERTS));
      playOrderAlertSound();
    }
  }, [lastMessage]);

  const acknowledgeOrderEvents = useCallback(() => {
    setOrderAlerts([]);
  }, []);

  const value = useMemo(
    () => ({
      lastMessage,
      messageVersion,
      pendingOrderAlertCount: orderAlerts.length,
      orderAlerts,
      latestOrderAlert: orderAlerts[0] || null,
      acknowledgeOrderEvents,
      isConnected,
      error,
    }),
    [lastMessage, messageVersion, orderAlerts, acknowledgeOrderEvents, isConnected, error]
  );

  return (
    <OrderSocketContext.Provider value={value}>
      {children}
    </OrderSocketContext.Provider>
  );
};

export const useOrderSocketEvents = () => useContext(OrderSocketContext);
